import { UseQueryResult } from '@tanstack/react-query'
import { Checkbox } from 'components/form/checkbox'
import { SearchField } from 'components/form/search-field'
import { useState } from 'react'
import { twJoin } from 'tailwind-merge'

const Item = ({
  label,
  onChange,
  readonly,
  selected,
}: {
  indeterminate?: boolean
  label: string
  onChange: (selected: boolean) => void
  readonly?: boolean
  selected: boolean
}) => {
  return (
    <label
      className={twJoin(
        'flex items-center gap-2 text-sm',
        readonly && 'cursor-not-allowed',
        selected && 'font-bold',
      )}
    >
      <Checkbox
        className={twJoin(readonly && 'opacity-50')}
        checked={selected}
        readOnly={readonly}
        onChange={() => {
          if (readonly) return
          onChange(!selected)
        }}
      />
      {label}
    </label>
  )
}

export const Picker = <T, R extends string | number>({
  allSelected,
  filter,
  idAccessor,
  label,
  onAllSelect,
  onSelect,
  placeholder,
  selectedIds,
  useQuery,
}: {
  allSelected: boolean
  filter: (entity: T, search: string) => boolean
  idAccessor: (entity: T) => R
  label: (entity: T) => string
  onAllSelect: (selected: boolean) => void
  onSelect: (entity: T, selected: boolean) => void
  placeholder: string
  selectedIds: Set<R>
  useQuery: () => UseQueryResult<T[], unknown>
}) => {
  const [search, setSearch] = useState('')

  const { data: data = [] } = useQuery()

  const filteredData = data.filter((d) => filter(d, search))

  return (
    <div className="flex flex-col gap-4 p-4 grow bg-neutral-100 rounded border-neutral-200 border overflow-hidden">
      <SearchField
        value={search}
        onChange={setSearch}
        placeholder={placeholder}
        searchFieldClassName="bg-white"
      />
      <div className="flex flex-col h-full gap-2 overflow-auto">
        <Item
          selected={allSelected}
          label="All"
          onChange={(selected) => onAllSelect(selected)}
        />
        {filteredData.map((item) => {
          const id = idAccessor(item)

          return (
            <Item
              key={id}
              selected={selectedIds.has(id) || allSelected}
              label={label(item)}
              onChange={(selected) => {
                onSelect(item, selected)
                onAllSelect(false)
              }}
              readonly={allSelected}
            />
          )
        })}
      </div>
    </div>
  )
}
